import React from 'react';
import _ from 'lodash';
import { Divider } from '@material-ui/core';

import {
  BooleanInput,
  Edit,
  FormTab,
  ReferenceArrayInput,
  SelectArrayInput,
  TabbedForm,
  SelectInput,
  RadioButtonGroupInput,
  FormDataConsumer,
  TextInput,
  CheckboxGroupInput,
  required,
} from 'react-admin';
import helper from './helper';
import './styles.css';
import disclaimerImage from '../../assets/images/informationVector.svg';

const Title = ({ record }) => (
  <span>Solicited User: <strong>{record.firstName} {record.lastName}</strong> ({record.email})</span>
);

const DisclaimerInformation = disclaimer => (
  <div className="disclaimerInfo">
    <img src={disclaimerImage} alt="info" className="disclaimerImage" />
    <div className="disclaimerText">{ disclaimer }</div>
  </div>
);

const representativesValidator = (value, allValues) => {
  if (allValues.solicited && _.isEmpty(value)) {
    return 'Required';
  }
  return undefined;
};

const solicitedAdmin = choice => `${choice.email} (${choice.role})`;
const SolicityTab = ({ record }) => (
  <ReferenceArrayInput
    record={record}
    source="representatives"
    reference="userRepresentatives"
    label="Representatives"
    basePath="users"
    filter={{ role: '_not_null_' }}
    validate={representativesValidator}
  >
    <SelectArrayInput optionText={solicitedAdmin} />
  </ReferenceArrayInput>
);

const stockValidation = typeOfStock => (value, allValues) => {
  if (allValues.suitabilityInformation[typeOfStock] && !value) {
    return 'Please select an option.';
  }
  return undefined;
};

const liquidityNeedsValidator = (value) => {
  if (!value) {
    return 'Please select an option.';
  }
  if (value === 'very_important') {
    return 'The denominator is invalid';
  }
  return undefined;
};

const riskToleranceValidator = (value) => {
  if (!value) {
    return 'Please select an option.';
  }
  if (value === 'conservative' || value === 'moderately_conservative' || value === 'moderate') {
    return 'The denominator is invalid';
  }
  return undefined;
};

const investmentObjectivesValidator = (value) => {
  if (!value) {
    return 'Please select an option.';
  }
  if (value === 'capital_preservation' || value === 'income' || value === 'growth') {
    return 'The denominator is invalid';
  }
  return undefined;
};

const timeHorizonValidator = (value) => {
  if (!value) {
    return 'Please select an option.';
  }
  if (value === 'short') {
    return 'The denominator is invalid';
  }
  return undefined;
};


const fundingMethodsValidator = (value, allValues) => {
  if (!_.isEmpty(allValues) &&
        !_.isEmpty(allValues.suitabilityInformation) &&
        _.isEmpty(allValues.suitabilityInformation.fundingMethods) &&
        _.isEmpty(allValues.suitabilityInformation.specialFundingMethod)) {
    return 'Please select at least one option.';
  }
  return undefined;
};

const decisionMakingValidator = (value) => {
  if (_.isEmpty(value)) {
    return 'Please select at least one option.';
  }
  return undefined;
};

const TypeOfStockExperience = ({ record, typeOfStock }) => (
  <div style={{
      display: 'flex',
      flex: 1,
      justifyContent: 'space-around',
    }}
  >
    <SelectInput
      autoWidth
      record={record}
      source={`suitabilityInformation.${typeOfStock}Values.yearsOfExperience`}
      label="Years of Experience"
      choices={helper.individualStocksYearOfExperienceValues}
      optionValue="id"
      optionText="value"
      validate={stockValidation(typeOfStock)}
    />
    <SelectInput
      autoWidth
      record={record}
      source={`suitabilityInformation.${typeOfStock}Values.transactionsPerYear`}
      label="Transaction Per Year"
      choices={helper.transactionNumberPerYearValues}
      optionValue="id"
      optionText="value"
      validate={stockValidation(typeOfStock)}
    />
  </div>
);

const FundingMethods = ({ record }) => (
  <div style={{ width: '100%' }} className="fundingMethodsWrapper">
    <CheckboxGroupInput
      record={record}
      label="How you are funding this account (check all that apply)"
      source="suitabilityInformation.fundingMethods"
      choices={helper.fundingMethodsValues}
      optionValue="id"
      optionText="value"
      validate={fundingMethodsValidator}
    />
    <TextInput
      fullWidth
      source="suitabilityInformation.specialFundingMethod"
      label="Special funding method"
      validate={fundingMethodsValidator}
    />
  </div>
);

export default props => (
  <Edit
    {...props}
    title={<Title />}
    undoable={false}
  >
    <TabbedForm>
      <FormTab label="Basic">
        <BooleanInput
          source="solicited"
          label="Solicited Account"
        />
        <SolicityTab />
      </FormTab>
      <FormTab label="Suitability Information">
        <SelectInput
          label="Tax Rate"
          source="suitabilityInformation.taxRate"
          choices={helper.taxRateValues}
          allowEmpty
          optionText="value"
          optionValue="id"
          validate={required('Please select a tax rate range.')}
        />
        <SelectInput
          label="Annual Expenses"
          source="suitabilityInformation.annualExpenses"
          choices={helper.expensesValues}
          allowEmpty
          optionText="value"
          optionValue="id"
          validate={required('Please select an annual expenses range.')}
        />
        <SelectInput
          label="Special Expenses"
          source="suitabilityInformation.specialExpenses"
          choices={helper.specialExpensesValues}
          allowEmpty
          optionText="value"
          optionValue="id"
          validate={required('Please select a special expenses range.')}
        />
        <SelectInput
          label="Time frame for special expenses"
          source="suitabilityInformation.timeFrameForSpecialExpenses"
          choices={helper.timeFrameForSpecialExpensesValues}
          allowEmpty
          optionText="value"
          optionValue="id"
          validate={required('Please select a time frame for special expenses.')}
        />
        <RadioButtonGroupInput
          label="Liquidity Needs"
          source="suitabilityInformation.liquidityNeeds"
          choices={helper.liquidityNeedsValues}
          allowEmpty
          fullWidth
          optionText={item => (<span>{item.value}<br /><i style={{ 'margin-top': '10px' }}>{item.subText}</i></span>)}
          validate={liquidityNeedsValidator}
          optionValue="id"
        />
        {DisclaimerInformation('In order to qualify for My IPO Offers your Liquidity Needs must be “Somewhat Important” or “Not Important”')}
        <Divider />
        <RadioButtonGroupInput
          label="Risk Tolerance"
          source="suitabilityInformation.riskTolerance"
          choices={helper.riskToleranceValues}
          allowEmpty
          fullWidth
          optionText={item => (<span>{item.value}<br /><i style={{ 'margin-top': '10px' }}>{item.subText}</i></span>)}
          optionValue="id"
          validate={riskToleranceValidator}
        />
        {DisclaimerInformation('In order to qualify for My IPO Offers your Risk Tolerance must be “Moderately Aggressive” or “Significant Risk”')}
        <RadioButtonGroupInput
          label="Investment Objectives"
          source="suitabilityInformation.investmentObjectives"
          choices={helper.investmentObjectivesValues}
          allowEmpty
          fullWidth
          optionValue="id"
          optionText={item => (<span>{item.value}<br /><i style={{ 'margin-top': '10px' }}>{item.subText}</i></span>)}
          validate={investmentObjectivesValidator}
        />
        {DisclaimerInformation('In order to qualify for My IPO Offers your Investment Objectives must be “Maximum Growth” or “Speculation”')}
        <Divider />
        <div className="financialInvestmentsWrapper">
          <table style={{ width: '80%' }}>
            <tr>
              <td colSpan="2">
                <span className="reactAdminLabel">Investment Experience (check all that apply)</span>
              </td>
            </tr>
            <tr>
              <td style={{ width: '40%' }}>
                <BooleanInput
                  label={_.find(helper.investmentExperienceDisplayableValues, { id: 'mutualFunds' }).label}
                  source="suitabilityInformation.mutualFunds"
                />
              </td>
              <td style={{ width: '60%' }}>
                <FormDataConsumer>
                  {({ formData }) => !_.isEmpty(formData) && formData.suitabilityInformation && formData.suitabilityInformation.mutualFunds &&
                  <TypeOfStockExperience typeOfStock="mutualFunds" />}
                </FormDataConsumer>
              </td>
            </tr>
            <tr>
              <td style={{ width: '40%' }}>
                <BooleanInput
                  label={_.find(helper.investmentExperienceDisplayableValues, { id: 'commodities' }).label}
                  source="suitabilityInformation.commodities"
                />
              </td>
              <td style={{ width: '60%' }}>
                <FormDataConsumer>
                  {({ formData }) => !_.isEmpty(formData) && formData.suitabilityInformation && formData.suitabilityInformation.commodities &&
                  <TypeOfStockExperience typeOfStock="commodities" />}
                </FormDataConsumer>
              </td>
            </tr>
            <tr>
              <td style={{ width: '40%' }}>
                <BooleanInput
                  label={_.find(helper.investmentExperienceDisplayableValues, { id: 'individualStocks' }).label}
                  source="suitabilityInformation.individualStocks"
                />
              </td>
              <td style={{ width: '60%' }}>
                <FormDataConsumer>
                  {({ formData }) => !_.isEmpty(formData) && formData.suitabilityInformation && formData.suitabilityInformation.individualStocks &&
                  <TypeOfStockExperience typeOfStock="individualStocks" />}
                </FormDataConsumer>
              </td>
            </tr>
            <tr>
              <td style={{ width: '40%' }}>
                <BooleanInput
                  label={_.find(helper.investmentExperienceDisplayableValues, { id: 'bonds' }).label}
                  source="suitabilityInformation.bonds"
                />
              </td>
              <td style={{ width: '60%' }}>
                <FormDataConsumer>
                  {({ formData }) => !_.isEmpty(formData) && formData.suitabilityInformation && formData.suitabilityInformation.bonds &&
                  <TypeOfStockExperience typeOfStock="bonds" />}
                </FormDataConsumer>
              </td>
            </tr>
            <tr>
              <td style={{ width: '40%' }}>
                <BooleanInput
                  label={_.find(helper.investmentExperienceDisplayableValues, { id: 'options' }).label}
                  source="suitabilityInformation.options"
                />
              </td>
              <td style={{ width: '60%' }}>
                <FormDataConsumer>
                  {({ formData }) => !_.isEmpty(formData) && formData.suitabilityInformation && formData.suitabilityInformation.options &&
                  <TypeOfStockExperience typeOfStock="options" />}
                </FormDataConsumer>
              </td>
            </tr>
            <tr>
              <td style={{ width: '40%' }}>
                <BooleanInput
                  label={_.find(helper.investmentExperienceDisplayableValues, { id: 'securitiesFutures' }).label}
                  source="suitabilityInformation.securitiesFutures"
                />
              </td>
              <td style={{ width: '60%' }}>
                <FormDataConsumer>
                  {({ formData }) => !_.isEmpty(formData) && formData.suitabilityInformation && formData.suitabilityInformation.securitiesFutures &&
                  <TypeOfStockExperience typeOfStock="securitiesFutures" />}
                </FormDataConsumer>
              </td>
            </tr>
            <tr>
              <td style={{ width: '40%' }}>
                <BooleanInput
                  label={_.find(helper.investmentExperienceDisplayableValues, { id: 'annuities' }).label}
                  source="suitabilityInformation.annuities"
                />
              </td>
              <td style={{ width: '60%' }}>
                <FormDataConsumer>
                  {({ formData }) => !_.isEmpty(formData) && formData.suitabilityInformation && formData.suitabilityInformation.annuities &&
                  <TypeOfStockExperience typeOfStock="annuities" />}
                </FormDataConsumer>
              </td>
            </tr>
            <tr>
              <td style={{ width: '40%' }}>
                <BooleanInput
                  label={_.find(helper.investmentExperienceDisplayableValues, { id: 'alternatives' }).label}
                  source="suitabilityInformation.alternatives"
                />
              </td>
              <td style={{ width: '60%' }}>
                <FormDataConsumer>
                  {({ formData }) => !_.isEmpty(formData) && formData.suitabilityInformation && formData.suitabilityInformation.alternatives &&
                  <TypeOfStockExperience typeOfStock="alternatives" />}
                </FormDataConsumer>
              </td>
            </tr>
            <tr>
              <td style={{ width: '40%' }}>
                <BooleanInput
                  label={_.find(helper.investmentExperienceDisplayableValues, { id: 'margin' }).label}
                  source="suitabilityInformation.margin"
                />
              </td>
              <td style={{ width: '60%' }}>
                <FormDataConsumer>
                  {({ formData }) => !_.isEmpty(formData) && formData.suitabilityInformation && formData.suitabilityInformation.margin &&
                  <TypeOfStockExperience typeOfStock="margin" />}
                </FormDataConsumer>
              </td>
            </tr>
            <tr>
              <td style={{ width: '40%' }}>
                <BooleanInput
                  label={_.find(helper.investmentExperienceDisplayableValues, { id: 'REITs' }).label}
                  source="suitabilityInformation.REITs"
                />
              </td>
              <td style={{ width: '60%' }}>
                <FormDataConsumer>
                  {({ formData }) => !_.isEmpty(formData) && formData.suitabilityInformation && formData.suitabilityInformation.REITs &&
                  <TypeOfStockExperience typeOfStock="REITs" />}
                </FormDataConsumer>
              </td>
            </tr>
          </table>
        </div>
        <CheckboxGroupInput
          label="Decision Making (check all that apply)"
          source="suitabilityInformation.decisionMaking"
          choices={helper.decisionMakingValues}
          optionValue="id"
          optionText="value"
          validate={decisionMakingValidator}
        />
        <Divider />
        <RadioButtonGroupInput
          fullWidth
          label="The investments in this account will be"
          source="suitabilityInformation.investmentPortfolio"
          choices={helper.investmentPortfolioValues}
          optionValue="id"
          optionText="value"
          validate={required('Please select an option')}
        />
        <RadioButtonGroupInput
          label="Time Horizon"
          source="suitabilityInformation.timeHorizon"
          choices={helper.timeHorizonValues}
          allowEmpty
          fullWidth
          optionValue="id"
          optionText={item => (<span>{item.value}<br /><i style={{ 'margin-top': '10px' }}>{item.subText}</i></span>)}
          validate={timeHorizonValidator}
        />
        {DisclaimerInformation('In order to qualify for My IPO Offers your Time Horizon must be “Average” or “Longest”')}
        <FundingMethods />
      </FormTab>
    </TabbedForm>
  </Edit>
);



// WEBPACK FOOTER //
// ./src/components/SolicitedUsers/Edit.js